import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type AmbulanceDestination, type AmbulanceFareEstimate } from '../../api';
import { onAmbulanceRequested } from './bookingUtils';

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
  padding: 16, boxShadow: T.shadow, ...extra,
});

const EMERGENCY_TYPES = [
  { key: 'CARDIAC', label: 'Cardiac', icon: '❤️', desc: 'Chest pain, heart attack' },
  { key: 'ACCIDENT', label: 'Accident / Trauma', icon: '🚨', desc: 'Road accident, fall, injury' },
  { key: 'STROKE', label: 'Stroke', icon: '🧠', desc: 'Sudden weakness, speech difficulty' },
  { key: 'RESPIRATORY', label: 'Breathing', icon: '🫁', desc: 'Severe breathlessness, asthma' },
  { key: 'MATERNITY', label: 'Maternity', icon: '👶', desc: 'Labor, pregnancy emergency' },
  { key: 'GENERAL', label: 'General', icon: '🏥', desc: 'Other medical emergency' },
];

const AMBULANCE_TYPES = [
  { key: 'BLS', label: 'BLS', desc: 'Basic Life Support' },
  { key: 'ALS', label: 'ALS', desc: 'Advanced Life Support' },
  { key: 'ICU', label: 'ICU Ambulance', desc: 'Intensive Care' },
  { key: 'NEONATAL', label: 'Neonatal', desc: 'Newborn transport' },
];

export default function AmbulancePage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('');
  const [ambulanceType, setAmbulanceType] = useState('BLS');
  const [pickupAddress, setPickupAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [destination, setDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [addressError, setAddressError] = useState(false);
  /**
   * Where a crew can take you, and what the published classes start at. Both
   * come from the ambulance module; neither is invented, and an empty fare
   * list is shown as "no fleet is quoting" rather than as a price.
   */
  const [destinations, setDestinations] = useState<AmbulanceDestination[]>([]);
  const [destinationId, setDestinationId] = useState('');
  const [fare, setFare] = useState<AmbulanceFareEstimate | null>(null);

  useEffect(() => {
    let alive = true;
    patientApi.ambulance.destinations()
      .then((rows) => { if (alive) setDestinations(rows ?? []); })
      .catch(() => { if (alive) setDestinations([]); });
    patientApi.ambulance.fareEstimate()
      .then((f) => { if (alive) setFare(f); })
      .catch(() => { if (alive) setFare(null); });
    return () => { alive = false; };
  }, []);

  const handleSOS = async () => {
    setErrorMsg(null);
    setAddressError(false);
    if (!selectedType) { setErrorMsg('Please select an emergency type.'); return; }
    if (!pickupAddress.trim()) { setAddressError(true); setErrorMsg('Pickup address is required.'); return; }
    const chosen = destinations.find((d) => d.providerId === destinationId);
    const destinationAddress = chosen?.address || destination.trim();
    if (!destinationAddress) {
      setErrorMsg('Choose a hospital, or type where the ambulance should take you.');
      return;
    }
    setSubmitting(true);
    try {
      // A real ambulance request, not an SOS note: this is the record a fleet
      // works from, and it comes back with a booking number the patient can
      // track and cancel. The success screen only ever shows for a request
      // the backend actually created — a failed call must NEVER look like a
      // dispatched ambulance.
      const booking = await patientApi.ambulance.book({
        requestType: 'EMERGENCY',
        ambulanceType,
        pickup: { address: pickupAddress.trim() },
        destination: {
          address: destinationAddress,
          ...(chosen ? { facilityName: chosen.name, hospitalProviderId: chosen.providerId, choiceMode: 'PREFERRED' } : { choiceMode: 'DECIDE_LATER' }),
        },
        triage: {
          conditions: [selectedType].filter(Boolean),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      });
      onAmbulanceRequested({
        id: booking.id,
        emergencyType: selectedType,
        ambulanceType,
        location: pickupAddress,
        destination: destinationAddress,
        notes: notes || undefined,
      });
      setResult(booking);
    } catch {
      setErrorMsg(
        'Your request could NOT be submitted — the server is unreachable. ' +
        'Do not wait: call 112 or 108 now for an ambulance.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div style={{ padding: 16, fontFamily: T.font, textAlign: 'center', paddingBottom: 100 }}>
        <div style={{ width: 64, height: 64, borderRadius: 32, background: '#10B98120', display: 'grid', placeItems: 'center', margin: '32px auto 16px' }}>
          <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text }}>Ambulance requested</h2>
        <p style={{ fontSize: 13, color: T.textSec, marginTop: 8 }}>
          Your request is with the ambulance service. Track it, or call it off, under Ambulance.
        </p>

        <div style={{ ...card({ marginTop: 16, background: '#EA580C08', textAlign: 'left' as const }) }}>
          <div style={{ fontSize: 10, color: T.textSec, marginBottom: 4 }}>Booking number</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#EA580C', letterSpacing: '0.03em' }}>{result.bookingNumber ?? result.id}</div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 8 }}>Status: <span style={{ fontWeight: 700, color: '#059669' }}>{result.status || 'RECORDED'}</span></div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>Type: <span style={{ fontWeight: 600, color: T.text }}>{selectedType} · {ambulanceType}</span></div>
          {destination && <div style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>Destination: <span style={{ fontWeight: 600, color: T.text }}>{destination}</span></div>}
        </div>

        <div style={{ fontSize: 11, color: T.textSec, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: 12, marginTop: 16, lineHeight: 1.5, textAlign: 'left' as const }}>
          <strong style={{ color: '#DC2626' }}>Important:</strong> A fleet still has to accept and dispatch this.
          If someone is in danger now, call 112 or 108 as well — do not wait on the app.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          <button onClick={() => navigate('/patient/ambulance')} style={{ width: '100%', padding: '12px 0', borderRadius: 10, background: '#EA580C', color: '#FFF', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', fontFamily: T.font }}>Track this ambulance</button>
          <button onClick={() => navigate('/patient/care-services')} style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSec, fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer', fontFamily: T.font }}>Back to Care Services</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, fontFamily: T.font, paddingBottom: 100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={() => navigate('/patient/care-services')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font, fontSize: 12, fontWeight: 600, color: '#EA580C' }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
          Back
        </button>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: '#EA580C12', display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8l3-5h10l4 5v9H3V8z" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /><path d="M10 6h4" /><path d="M12 4v4" />
          </svg>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>Ambulance</h2>
        <p style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>Emergency and scheduled ambulance services</p>
      </div>

      <button onClick={() => navigate('/patient/emergency')} style={{ ...card({ marginBottom: 16, background: '#FEF2F2', border: '1px solid #FECACA', width: '100%', cursor: 'pointer', textAlign: 'left' as const }), display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#DC262620', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round"><path d="M12 9v4" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="10" /></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626' }}>Emergency SOS</div>
          <div style={{ fontSize: 11, color: '#991B1B', marginTop: 2 }}>Immediate emergency response with location sharing</div>
        </div>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
      </button>

      <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Request Ambulance</h3>

      <div style={{ fontSize: 12, fontWeight: 600, color: T.textSec, marginBottom: 8 }}>Emergency Type</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
        {EMERGENCY_TYPES.map(t => (
          <button key={t.key} onClick={() => setSelectedType(t.key)} style={{
            ...card({ padding: '12px 8px', textAlign: 'center' as const, cursor: 'pointer' }),
            border: selectedType === t.key ? `2px solid #EA580C` : `1px solid ${T.border}`,
            background: selectedType === t.key ? '#EA580C08' : T.white,
          }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.text }}>{t.label}</div>
          </button>
        ))}
      </div>

      {/* Ambulance Type */}
      <div style={{ fontSize: 12, fontWeight: 600, color: T.textSec, marginBottom: 8 }}>Ambulance Type</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {AMBULANCE_TYPES.map(t => (
          <button key={t.key} onClick={() => setAmbulanceType(t.key)} style={{
            fontFamily: T.font,
            fontSize: 11,
            fontWeight: 600,
            color: ambulanceType === t.key ? T.white : T.textSec,
            background: ambulanceType === t.key ? '#EA580C' : T.white,
            border: `1.5px solid ${ambulanceType === t.key ? '#EA580C' : T.border}`,
            borderRadius: 20,
            padding: '6px 14px',
            cursor: 'pointer',
            transition: 'background 0.15s, color 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 4 }}>Pickup Address *</label>
        <textarea value={pickupAddress} onChange={e => { setPickupAddress(e.target.value); setAddressError(false); }} placeholder="Full address or landmark..." style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${addressError ? '#DC2626' : T.border}`, fontSize: 13, fontFamily: T.font, resize: 'vertical' as const, minHeight: 60, boxSizing: 'border-box' as const }} />
        {addressError && <p style={{ fontFamily: T.font, fontSize: 11, color: '#DC2626', margin: '4px 0 0' }}>Pickup address is required</p>}
      </div>

      {/* Where the crew should take you — the hospitals this service reaches,
          with the emergency beds each reports free. */}
      {destinations.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="amb-destination" style={{ fontSize: 12, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 4 }}>
            Take me to
          </label>
          <select
            id="amb-destination"
            value={destinationId}
            onChange={(e) => { setDestinationId(e.target.value); setDestination(''); }}
            style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.text, background: T.white }}
          >
            <option value="">Let the crew decide, or type one below</option>
            {destinations.map((d) => (
              <option key={d.providerId} value={d.providerId}>
                {d.name}{d.erBedsFree != null ? ` — ${d.erBedsFree > 0 ? `${d.erBedsFree} ER beds free` : 'ER full'}` : ''}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 10, color: T.textTer, marginTop: 4, lineHeight: 1.5 }}>
            Bed counts are each hospital's own figure.
          </div>
        </div>
      )}

      {/* What the classes start at, when a fleet is publishing prices. */}
      <div style={{
        marginBottom: 12, background: T.ice, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: '10px 12px', fontSize: 11, color: T.textSec, lineHeight: 1.5,
      }}>
        {fare && fare.fleetsQuoting > 0 && fare.low != null
          ? `Published fares start between ₹${fare.low} and ₹${fare.high ?? fare.low} — ${fare.fleetsQuoting} of ${fare.fleetsTotal} fleets are quoting. The crew confirms the final fare.`
          : `No fleet is publishing a fare right now${fare ? ` (${fare.fleetsTotal} in range)` : ''} — the service tells you the cost when they accept.`}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 4 }}>
          {destinationId ? 'Or a different address' : 'Destination address'}
        </label>
        <input value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g. Apollo Hospital, Sarita Vihar" style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, boxSizing: 'border-box' as const }} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 4 }}>Patient Notes (optional)</label>
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Patient condition, floor, gate..." style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, boxSizing: 'border-box' as const }} />
      </div>

      <p style={{ fontSize: 10, color: T.textTer, lineHeight: 1.6, marginBottom: 12 }}>
        By requesting, you consent to sharing your location and medical details with the ambulance provider. In a real emergency, always call 112 or 108 directly.
      </p>

      {errorMsg && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
          <p style={{ fontFamily: T.font, fontSize: 12, color: '#B91C1C', margin: 0 }}>{errorMsg}</p>
        </div>
      )}

      <button onClick={handleSOS} disabled={submitting || !selectedType} style={{
        width: '100%', padding: '14px 0', borderRadius: 12,
        background: selectedType ? '#EA580C' : '#EA580C60', color: '#FFF',
        fontWeight: 700, fontSize: 14, border: 'none', cursor: selectedType ? 'pointer' : 'default',
        fontFamily: T.font, opacity: submitting ? 0.7 : 1,
      }}>
        {submitting ? 'Requesting...' : 'Request Ambulance Now'}
      </button>

      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Emergency ambulance request · A fleet has to accept your request · Every request is audit logged
        </span>
      </div>
    </div>
  );
}
